// utils/retryLogic/manager.ts - utils/retryLogic.ts split (sub-module manager)


import { DEFAULT_OPTIONS } from './backoff.js';
import type {RetryOptions} from './types.js';
import type {RetryStatus} from './types.js';
import { calculateExponentialBackoff } from './backoff.js';
import { sleep } from './backoff.js';



export class RetryManager {
  private statusListeners: Set<(status: RetryStatus) => void> = new Set();
  private status: RetryStatus = {
    isRetrying: false,
    attempt: 0,
    maxRetries: 0,
    nextRetryInMs: 0,
    lastError: null,
  };

  public subscribe(listener: (status: RetryStatus) => void) {
    this.statusListeners.add(listener);
    // initial state
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  private updateStatus(newStatus: Partial<RetryStatus>) {
    this.status = { ...this.status, ...newStatus };
    this.statusListeners.forEach(listener => listener(this.status));
  }

  public getStatus() {
    return this.status;
  }

  public async execute<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
  ): Promise<T> {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    this.updateStatus({
      isRetrying: false,
      attempt: 0,
      maxRetries: opts.maxRetries,
      nextRetryInMs: 0,
      lastError: null
    });

    let attempt = 1;
    let lastError: any;

    while (attempt <= opts.maxRetries) {
      try {
        const result = await operation();
        // Success
        this.updateStatus({ isRetrying: false, lastError: null });
        return result;
      } catch (error) {
        lastError = error;

        const shouldRetry = opts.shouldRetry ? opts.shouldRetry(error) : true;
        
        if (!shouldRetry || attempt === opts.maxRetries) {
          this.updateStatus({ isRetrying: false, lastError: error });
          throw error;
        }

        const delayMs = calculateExponentialBackoff(
          attempt,
          opts.initialDelayMs,
          opts.maxDelayMs,
          opts.backoffMultiplier
        );

        if (opts.onRetry) {
          opts.onRetry(attempt, delayMs, error);
        }

        this.updateStatus({
          isRetrying: true,
          attempt,
          nextRetryInMs: delayMs,
          lastError: error
        });

        await sleep(delayMs);
        attempt++;
      }
    }

    throw lastError;
  }
}
